import { useState } from "react";
import {
  ChevronDown,
  Person,
  VolumeUp,
  Bell,
  Clock,
  Telephone,
  GeoAlt,
  CheckLg,
  XLg,
} from "react-bootstrap-icons";
import styles from "./DeviceSections.module.css";

function AccordionItem({ icon, title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={styles.accordionItem}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={styles.accordionHeader}
      >
        <div className={styles.accordionIcon}>{icon}</div>
        <div className={styles.accordionMeta}>
          <p className={styles.accordionTitle}>{title}</p>
          {subtitle ? <p className={styles.accordionSubtitle}>{subtitle}</p> : null}
        </div>
        <ChevronDown
          size={18}
          className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
        />
      </button>
      {open && (
        <div className={styles.accordionBody}>{children}</div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue}>{value}</span>
    </div>
  );
}

function BoolBadge({ value }) {
  return value ? (
    <span className={`${styles.badge} ${styles.badgeGreen}`}>
      <CheckLg size={10} /> Yes
    </span>
  ) : (
    <span className={`${styles.badge} ${styles.badgeSlate}`}>
      <XLg size={10} /> No
    </span>
  );
}

function EnabledBadge({ value }) {
  return value ? (
    <span className={`${styles.badge} ${styles.badgeBlue}`}>
      Enabled
    </span>
  ) : (
    <span className={`${styles.badge} ${styles.badgeSlate}`}>
      Disabled
    </span>
  );
}

function VolumeBar({ value }) {
  return (
    <div className={styles.volumeRow}>
      <div className={styles.volumeTrack}>
        <div className={styles.volumeFill} style={{ width: `${value}%` }} />
      </div>
      <span className={styles.volumeText}>{value}%</span>
    </div>
  );
}

export default function DeviceSettingsSection({ settings }) {
  const activeContacts = Object.entries(settings.contact_numbers || {}).filter(
    ([, contact]) => contact.enable,
  );

  const activeGeoFences = Object.entries(settings.geo_fences || {}).filter(
    ([, fence]) => fence.enable,
  );

  return (
    <div className={styles.sectionStack}>
      <AccordionItem
        icon={<VolumeUp size={16} />}
        title="Volume Settings"
        subtitle="Ringtone, Microphone, Speaker, Voice Prompt"
        defaultOpen
      >
        <div className={styles.sectionStack}>
          {[
            { label: "Ringtone Volume", value: settings.ringtone_volume },
            { label: "Microphone Volume", value: settings.microphone_volume },
            { label: "Speaker Volume", value: settings.speaker_volume },
            { label: "Voice Prompt Volume", value: settings.voice_prompt_volume },
          ].map(({ label, value }) => (
            <div key={label} className={styles.row}>
              <span className={styles.rowLabel}>{label}</span>
              <VolumeBar value={value} />
            </div>
          ))}
        </div>
      </AccordionItem>

      <AccordionItem
        icon={<Bell size={16} />}
        title="Alert Settings"
        subtitle="Motion, Fall, Tilt, Speed, No-Motion"
        defaultOpen
      >
        <div className={styles.alertGroups}>
          <div>
            <div className={styles.sectionHeaderRow}>
              <p className={styles.innerTitle}>No Motion Alert</p>
              <EnabledBadge value={settings.no_motion_alert.enable} />
            </div>
            <div className={styles.innerRows}>
              <Row label="Duration" value={`${settings.no_motion_alert.duration}s`} />
              <Row label="Receive Call" value={<BoolBadge value={settings.no_motion_alert.dial} />} />
            </div>
          </div>

          <div>
            <div className={styles.sectionHeaderRow}>
              <p className={styles.innerTitle}>Over Speed Alert</p>
              <EnabledBadge value={settings.over_speed_alert.enable} />
            </div>
            <div className={styles.innerRows}>
              <Row label="Speed Threshold" value={`${settings.over_speed_alert.speed_threshold} km/h`} />
            </div>
          </div>

          <div>
            <div className={styles.sectionHeaderRow}>
              <p className={styles.innerTitle}>Tilt Alert</p>
              <EnabledBadge value={settings.tilt_alert.enable} />
            </div>
            <div className={styles.innerRows}>
              <Row label="Time Threshold" value={`${settings.tilt_alert.time_threshold}s`} />
              <Row label="Angle Threshold" value={`${settings.tilt_alert.angle_threshold}°`} />
              <Row label="Receive Call" value={<BoolBadge value={settings.tilt_alert.dial} />} />
            </div>
          </div>

          <div>
            <div className={styles.sectionHeaderRow}>
              <p className={styles.innerTitle}>Fall Down Alert</p>
              <EnabledBadge value={settings.falldown_alert.enable} />
            </div>
            <div className={styles.innerRows}>
              <Row label="Sensitive Level" value={settings.falldown_alert.sensitive_level} />
              <Row label="Always On" value={<BoolBadge value={settings.falldown_alert.always_on} />} />
              <Row label="Receive Call" value={<BoolBadge value={settings.falldown_alert.dial} />} />
            </div>
          </div>

          <div>
            <div className={styles.sectionHeaderRow}>
              <p className={styles.innerTitle}>Motion Alert</p>
              <EnabledBadge value={settings.motion_alert.enable} />
            </div>
            <div className={styles.innerRows}>
              <Row label="Duration" value={`${settings.motion_alert.duration}s`} />
              <Row label="Receive Call" value={<BoolBadge value={settings.motion_alert.dial} />} />
            </div>
          </div>
        </div>
      </AccordionItem>

      <AccordionItem
        icon={<Clock size={16} />}
        title="Work Mode Settings"
        subtitle={`Work Mode ${settings.time_interval.work_mode}`}
        defaultOpen
      >
        <div className={styles.dividedList}>
          <Row label="Enable Heartbeat / Ping Interval for Device" value={<EnabledBadge value={settings.time_interval.heartbeat_enable} />} />
          <Row label="Reporting Interval When Moving" value={`${settings.time_interval.report_interval}s`} />
          <Row label="Reporting Interval When Not Moving" value={`${settings.time_interval.lazy_report_interval}s`} />
          <Row label="Device Ping / Heartbeat Interval" value={`${settings.time_interval.heartbeat_interval}s`} />
          <Row label="Select Work Mode" value={settings.time_interval.work_mode} />
        </div>
      </AccordionItem>

      <AccordionItem
        icon={<Telephone size={16} />}
        title="SOS Alert/Emails"
        subtitle={`${activeContacts.length} active contact(s)`}
        defaultOpen
      >
        {activeContacts.length === 0 ? (
          <p className={styles.emptyText}>No contacts configured</p>
        ) : (
          <div className={styles.sectionStack}>
            {activeContacts.map(([key, contact]) => (
              <div key={key} className={styles.boxCard}>
                <div className={styles.sectionHeaderRow}>
                  <span className={styles.boxCardTitle}>{contact.number}</span>
                  <span className={`${styles.badge} ${styles.badgeGreen}`}>
                    Contact {Number(key) + 1}
                  </span>
                </div>
                <div className={styles.contactRow}>
                  <span
                    className={`${styles.contactFlag} ${
                      contact.accept_call ? styles.contactFlagEnabled : styles.contactFlagDisabled
                    }`}
                  >
                    <CheckLg size={10} /> Calls
                  </span>
                  <span
                    className={`${styles.contactFlag} ${
                      contact.accept_sms ? styles.contactFlagEnabled : styles.contactFlagDisabled
                    }`}
                  >
                    <CheckLg size={10} /> SMS
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </AccordionItem>

      <AccordionItem
        icon={<GeoAlt size={16} />}
        title="Geo Fence Alert"
        subtitle={`${activeGeoFences.length} active fence(s)`}
        defaultOpen
      >
        {activeGeoFences.length === 0 ? (
          <p className={styles.emptyText}>No active geo fences</p>
        ) : (
          <div className={styles.sectionStack}>
            {Object.entries(settings.geo_fences).map(([key, fence]) => {
              const label = settings.geo_fence_labels[String(Number(key) + 1)] || `Zone ${Number(key) + 1}`;
              if (!fence.enable && fence.radius === 0) return null;
              return (
                <div key={key} className={styles.boxCard}>
                  <div className={styles.sectionHeaderRow}>
                    <span className={styles.boxCardTitle}>{label || `Zone ${Number(key) + 1}`}</span>
                    <EnabledBadge value={fence.enable} />
                  </div>
                  <div className={styles.dividedList}>
                    <Row label="Type" value={fence.type} />
                    <Row label="Radius" value={`${fence.radius}m`} />
                    {fence.points.length > 0 && (
                      <Row
                        label="Center"
                        value={`${fence.points[0].lat.toFixed(4)}, ${fence.points[0].lng.toFixed(4)}`}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </AccordionItem>
    </div>
  );
}
